import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Zap, Crown, Sparkles, AlertCircle, Loader } from 'lucide-react';
import { apiClient } from '../../../services/apiClient';

const PLAN_ICONS = [Zap, Crown, Sparkles];

const ChatbotPlans = () => {
  const navigate = useNavigate();
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [plans, setPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [plansError, setPlansError] = useState(false);
  const [annualDiscount, setAnnualDiscount] = useState(null);

  useEffect(() => {
    apiClient.get('/api/subscriptions/chatbot/plans')
      .then(data => {
        const arr = Array.isArray(data) ? data : (data.content ?? []);
        setPlans(arr);
        const ref = arr.find(p => Number(p.monthlyPrice) > 0 && Number(p.annualPrice) > 0);
        if (ref) {
          const pct = Math.round((1 - Number(ref.annualPrice) / (Number(ref.monthlyPrice) * 12)) * 100);
          setAnnualDiscount(pct > 0 ? pct : null);
        }
      })
      .catch(() => setPlansError(true))
      .finally(() => setLoadingPlans(false));
  }, []);

  const handleSubscribe = (plan) => {
    navigate('/portal/checkout', {
      state: { plan, billingCycle, serviceType: 'CHATBOT' },
    });
  };

  const getOverage = (plan) => {
    if (!plan.overageEnabled) return [];
    if (plan.overagePricePerMessage != null) {
      return [`Additional messages: $${Number(plan.overagePricePerMessage).toFixed(5)}/message`];
    }
    if (plan.pricePerAdditional1k != null) {
      return [`Additional messages: $${Number(plan.pricePerAdditional1k).toFixed(2)}/1,000 messages`];
    }
    return [];
  };

  if (loadingPlans) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  if (plansError) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 flex items-center gap-3">
          <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0" />
          <div>
            <p className="font-semibold text-red-900">Failed to load plans</p>
            <p className="text-sm text-red-700 mt-1">Please try again or contact support.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Choose Your AI Chatbot Plan
        </h1>
        <p className="text-gray-600">
          Unlock the power of AI-driven customer support
        </p>
      </div>

      {/* Billing Toggle */}
      <div className="flex justify-center mb-12">
        <div className="bg-gray-100 p-1 rounded-lg inline-flex">
          <button
            onClick={() => setBillingCycle('monthly')}
            className={`px-6 py-2 rounded-md font-medium transition-all ${
              billingCycle === 'monthly'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Monthly
          </button>
          <button
            onClick={() => setBillingCycle('annual')}
            className={`px-6 py-2 rounded-md font-medium transition-all ${
              billingCycle === 'annual'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Annual
            {annualDiscount != null && (
              <span className="ml-2 text-xs text-green-600 font-semibold">Save {annualDiscount}%</span>
            )}
          </button>
        </div>
      </div>

      {/* Plans */}
      <div className="grid md:grid-cols-3 gap-8 max-w-7xl mx-auto">
        {plans.map((plan, index) => {
          const Icon = PLAN_ICONS[index] ?? Sparkles;
          const highlighted = plan.popular || index === 1;
          const price = billingCycle === 'monthly'
            ? Number(plan.monthlyPrice) || 0
            : Number(plan.annualPrice) || 0;
          const priceLabel = billingCycle === 'monthly' ? '/month' : '/year';
          const features = Array.isArray(plan.features) ? plan.features : [];
          const overage = getOverage(plan);
          return (
            <div
              key={plan.id}
              className={`relative bg-white rounded-2xl shadow-lg overflow-hidden transition-all duration-300 hover:shadow-2xl ${
                highlighted ? 'border-2 border-purple-500 transform scale-105' : 'border border-gray-200'
              }`}
            >
              {highlighted && (
                <div className="absolute top-0 right-0">
                  <div className="bg-purple-500 text-white px-4 py-1 text-xs font-bold rounded-bl-lg">
                    Most Popular
                  </div>
                </div>
              )}

              <div className="p-8">
                {/* Icon & Name */}
                <div className="flex items-center gap-3 mb-4">
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                    highlighted
                      ? 'bg-gradient-to-br from-purple-500 to-purple-600'
                      : 'bg-gradient-to-br from-gray-400 to-gray-600'
                  }`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">
                    {plan.planName || plan.name}
                  </h3>
                </div>

                <p className="text-gray-600 mb-6">{plan.description}</p>

                {/* Price */}
                <div className="mb-6">
                  {price === 0 ? (
                    <div className="text-3xl font-bold text-gray-900">Custom</div>
                  ) : (
                    <>
                      <div className="flex items-baseline gap-1">
                        <span className="text-4xl font-bold text-gray-900">${price}</span>
                        <span className="text-gray-600">{priceLabel}</span>
                      </div>
                      {billingCycle === 'annual' && (
                        <p className="text-sm text-gray-500 mt-1">
                          ${(price / 12).toFixed(2)}/month billed annually
                        </p>
                      )}
                    </>
                  )}
                  {plan.messagesPerMonth != null && (
                    <p className="text-sm text-purple-600 font-medium mt-2">
                      {Number(plan.messagesPerMonth).toLocaleString()} messages/month
                    </p>
                  )}
                </div>

                {/* Subscribe Button */}
                <button
                  onClick={() => handleSubscribe(plan)}
                  className={`w-full py-3 px-6 rounded-lg font-semibold transition-all mb-6 ${
                    highlighted
                      ? 'bg-purple-600 text-white hover:bg-purple-700 shadow-lg hover:shadow-xl'
                      : 'bg-gray-900 text-white hover:bg-gray-800'
                  }`}
                >
                  Subscribe Now
                </button>

                {/* Features */}
                {features.length > 0 && (
                  <div className="space-y-3">
                    <p className="text-sm font-semibold text-gray-900 mb-3">What's included:</p>
                    {features.map((feature, i) => (
                      <div key={i} className="flex items-start gap-3">
                        <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-gray-700">{feature}</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Overage */}
                {overage.length > 0 && (
                  <div className="mt-4 bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <div className="flex items-center gap-1.5 mb-2">
                      <AlertCircle className="w-3.5 h-3.5 text-amber-600 flex-shrink-0" />
                      <p className="text-xs font-semibold text-amber-800">Additional usage costs</p>
                    </div>
                    {overage.map((item, i) => (
                      <p key={i} className="text-xs text-amber-700 leading-relaxed">{item}</p>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Additional Info */}
      <div className="mt-16 max-w-4xl mx-auto">
        <div className="bg-purple-50 rounded-xl p-8 text-center">
          <h3 className="text-xl font-bold text-gray-900 mb-2">
            Need more messages or a custom plan?
          </h3>
          <p className="text-gray-600 mb-4">
            Contact our sales team for enterprise solutions and volume discounts
          </p>
          <button
            onClick={() => navigate('/portal/support')}
            className="btn btn-outline"
          >
            Contact Sales
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChatbotPlans;
